import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { X, Sparkle, RefreshCw, FileText, Search, Cog, Save, Upload, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { submitToExtension, ExtensionNoticeModal } from './ExtensionSubmit';
import { saveQuerySetup, generateText } from '../../lib/QueryDataService';

const AiContentModal = ({ 
  isOpen, 
  onClose, 
  onChangeProfile,
  selectedProfile,
  selectedResult,
  resultTitle,
  resultUrl,
  disabled = false 
}) => {
  const [aiNotes, setAiNotes] = useState('');
  const [aiGeneratedContent, setAiGeneratedContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSetup, setIsLoadingSetup] = useState(false);
  const [showAiSection, setShowAiSection] = useState(true); // Control AI section visibility
  const [showTextbox, setShowTextbox] = useState(false); // Control textbox visibility
  const [showProgressPopup, setShowProgressPopup] = useState(false); // Control progress popup visibility
  const [progressStep, setProgressStep] = useState(1); // Current progress step
  const [showExtensionModal, setShowExtensionModal] = useState(false); // Control extension notice modal
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false); // Control save confirmation modal
  const [selectedTemplate, setSelectedTemplate] = useState(''); // Selected template from dropdown
  const [selectedDestination, setSelectedDestination] = useState(null); // Selected destination for message
  const [destinationOptions, setDestinationOptions] = useState([]); // Available destination options
  const [showCopiedNotification, setShowCopiedNotification] = useState(false); // Show copied notification
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  // Business contact templates
  const templates = {
    en: [
      {
        id: 'business-partnership',
        label: 'Discuss Business Partnership',
        text: "I'd like to discuss a potential business partnership opportunity that could be mutually beneficial for both our companies. I believe there's great potential for collaboration that could help both of us expand our market reach and provide enhanced value to our customers."
      },
      {
        id: 'product-service-offer',
        label: 'Introduce Product/Service',
        text: "I have a product/service that I believe could add significant value to your business operations. I'd love to schedule a brief call to explore how this could benefit your company and discuss potential collaboration opportunities."
      },
      {
        id: 'link-exchange',
        label: 'Propose Link Exchange',
        text: "I'm interested in exploring link exchange opportunities between our websites to improve both of our SEO efforts and provide additional value to our respective audiences. Our sites seem to complement each other well."
      },
      {
        id: 'connect-decision-maker',
        label: 'Connect with Decision Maker',
        text: "I'd like to connect with the appropriate decision maker regarding potential collaboration opportunities. I have some ideas that could be of mutual interest and would appreciate the opportunity to present them."
      },
      {
        id: 'strategic-alliance',
        label: 'Strategic Business Alliance',
        text: "I believe there's potential for a strategic alliance between our businesses that could create significant value for both parties. Would you be open to a conversation about how we might work together?"
      },
      {
        id: 'advertising-sponsorship',
        label: 'Advertising/Sponsorship Opportunity',
        text: "I'm reaching out to explore potential advertising or sponsorship opportunities on your platform. I believe our brand would be a great fit for your audience and could provide valuable exposure for both of us."
      },
      {
        id: 'customer-collaboration',
        label: 'Customer Base Collaboration',
        text: "I'd like to discuss how we might collaborate to provide enhanced value to both our customer bases. I see opportunities for cross-promotion and joint initiatives that could benefit everyone involved."
      },
      {
        id: 'market-expansion',
        label: 'Market Expansion Partnership',
        text: "I have a proposal for a business partnership focused on market expansion that could help both our companies reach new audiences and grow our businesses. Would you be interested in exploring this opportunity?"
      }
    ],
    he: [
      {
        id: 'business-partnership',
        label: 'דיון בשותפות עסקית',
        text: "אני מעוניין לדון בהזדמנות שיתוף עסקי שיכולה להועיל לשני הצדדים. אני מאמין שיש פוטנציאל רב לשיתוף פעולה שיכול לעזור לשנינו להרחיב את הטווח השיווקי ולספק ערך משופר ללקוחותינו."
      },
      {
        id: 'product-service-offer',
        label: 'הצגת מוצר/שירות',
        text: "יש לי מוצר/שירות שאני מאמין שיכול להוסיף ערך משמעותי לפעילות העסקית שלכם. אשמח לתאם שיחה קצרה כדי לבחון כיצד זה יכול להועיל לחברה שלכם ולדון בהזדמנויות שיתוף אפשריות."
      },
      {
        id: 'link-exchange',
        label: 'הצעת חילופי קישורים',
        text: "אני מעוניין לבחון אפשרויות חילופי קישורים בין האתרים שלנו לשיפור מאמצי ה-SEO של שנינו ולמתן ערך נוסף לקהלים שלנו. נראה שהאתרים שלנו משלימים זה את זה היטב."
      },
      {
        id: 'connect-decision-maker',
        label: 'יצירת קשר עם מקבל החלטות',
        text: "אני מעוניין ליצור קשר עם מקבל ההחלטות המתאים בנוגע להזדמנויות שיתוף אפשריות. יש לי כמה רעיונות שיכולים לעניין הדדית ואשמח לקבל הזדמנות להציגם."
      },
      {
        id: 'strategic-alliance',
        label: 'ברית עסקית אסטרטגית',
        text: "אני מאמין שיש פוטנציאל לברית אסטרטגית בין העסקים שלנו שיכולה ליצור ערך משמעותי לשני הצדדים. האם תהיו פתוחים לשיחה על כיצד נוכל לעבוד יחד?"
      },
      {
        id: 'advertising-sponsorship',
        label: 'הזדמנות פרסום/חסות',
        text: "אני פונה כדי לבחון הזדמנויות פרסום או חסות אפשריות בפלטפורמה שלכם. אני מאמין שהמותג שלנו יתאים מאוד לקהל שלכם ויכול לספק חשיפה יקרת ערך לשנינו."
      },
      {
        id: 'customer-collaboration',
        label: 'שיתוף פעולה בבסיס הלקוחות',
        text: "אני מעוניין לדון כיצד נוכל לשתף פעולה כדי לספק ערך משופר לשני בסיסי הלקוחות שלנו. אני רואה הזדמנויות לקידום מוצלב ויוזמות משותפות שיכולות להועיל לכל המעורבים."
      },
      {
        id: 'market-expansion',
        label: 'שותפות להרחבת שוק',
        text: "יש לי הצעה לשותפות עסקית המתמקדת בהרחבת שוק שיכולה לעזור לשתי החברות שלנו להגיע לקהלים חדשים ולהצמיח את העסקים שלנו. האם תהיו מעוניינים לבחון את ההזדמנות הזו?"
      }
    ]
  };

  // Get domain from URL for button text
  const getDomainFromUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  };

  // Get queryId from URL path
  const getQueryIdFromUrl = () => {
    const pathname = window.location.pathname;
    return pathname.startsWith('/query/') ? pathname.split('/query/')[1]?.split('?')[0] : null;
  };

  // Save template content to localStorage by queryId
  const saveTemplateToLocalStorage = () => {
    const queryId = getQueryIdFromUrl();
    if (queryId && aiNotes.trim()) {
      const storageKey = `templateContent_${queryId}`;
      localStorage.setItem(storageKey, aiNotes.trim());
      console.log('Template content saved for queryId:', queryId);
    }
  };

  // Handle template selection from dropdown
  const handleTemplateSelect = (templateId) => {
    const template = getCurrentLanguageTemplates().find(t => t.id === templateId);
    if (template) {
      setAiNotes(template.text);
      setSelectedTemplate(templateId);
      setShowTextbox(true); // Show textbox when template is selected
    }
  };

  // Get current language templates
  const getCurrentLanguageTemplates = () => {
    const currentLang = isRTL ? 'he' : 'en'; // Use RTL detection to determine language
    return templates[currentLang] || templates.en;
  };

  // Load existing setup data when modal opens
  useEffect(() => {
    const loadSetupData = async () => {
      if (isOpen && selectedResult?.id) {
        setIsLoadingSetup(true);
        try {
          // Load saved template content from localStorage for this query
          const pathname = window.location.pathname;
          const queryId = pathname.startsWith('/query/') ? pathname.split('/query/')[1]?.split('?')[0] : null;
          
          if (queryId) {
            const storageKey = `templateContent_${queryId}`;
            const savedTemplate = localStorage.getItem(storageKey);
            
            if (savedTemplate) {
              setAiNotes(savedTemplate);
              setShowTextbox(true); // Show textbox if there's saved content
              // Find the matching template to set the dropdown
              const currentLang = isRTL ? 'he' : 'en';
              const currentTemplates = templates[currentLang] || templates.en;
              const matchingTemplate = currentTemplates.find(t => t.text === savedTemplate);
              if (matchingTemplate) {
                setSelectedTemplate(matchingTemplate.id);
              }
              console.log('Loaded saved template content for query');
            }
          }
        } catch (error) {
          console.log('Error loading data:', error.message);
        } finally {
          setIsLoadingSetup(false);
        }
      }
    };

    if (isOpen) {
      loadSetupData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedResult?.id, isRTL]);

  // Reset modal state when closing
  useEffect(() => {
    if (!isOpen) {
      // Reset all state to initial values
      setAiNotes('');
      setAiGeneratedContent('');
      setShowAiSection(true);
      setShowTextbox(false);
      setShowProgressPopup(false);
      setProgressStep(1);
      setShowExtensionModal(false);
      setShowSaveConfirmation(false);
      setSelectedTemplate('');
      setIsGenerating(false);
      setIsSubmitting(false);
      setIsLoadingSetup(false);
      setSelectedDestination(null);
      setDestinationOptions([]);
      setShowCopiedNotification(false);
    }
  }, [isOpen]);

  // Parse contacts and build destination options
  useEffect(() => {
    if (!isOpen || !selectedResult) return;

    const options = [];

    // 1. Add Website option (if exists) - default
    if (resultUrl) {
      const websiteOption = {
        type: 'website',
        label: t('website'),
        value: resultUrl,
        display: `${t('website')}: ${getDomainFromUrl(resultUrl)}`
      };
      options.push(websiteOption);
    }

    // 2. Add Email options (each email separately)
    if (selectedResult.contacts) {
      try {
        const contactsData = typeof selectedResult.contacts === 'string'
          ? JSON.parse(selectedResult.contacts)
          : selectedResult.contacts;

        if (contactsData?.emails && Array.isArray(contactsData.emails)) {
          contactsData.emails.forEach((email, index) => {
            options.push({
              type: 'email',
              label: `Email`,
              value: email,
              display: `Email: ${email}`
            });
          });
        }

        // 3. Add Social options
        if (contactsData?.socials && typeof contactsData.socials === 'object') {
          Object.entries(contactsData.socials).forEach(([platform, url]) => {
            const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
            options.push({
              type: 'social',
              label: platformName,
              value: url,
              display: platformName,
              platform: platform
            });
          });
        }
      } catch (error) {
        console.error('Error parsing contacts for destination options:', error);
      }
    }

    setDestinationOptions(options);
    
    // Auto-select first option (priority: website > email > social)
    if (options.length > 0) {
      setSelectedDestination(options[0]);
    }
  }, [isOpen, selectedResult, resultUrl, t]);

  const handleGenerateContent = async () => {
    if (!aiNotes.trim() || !selectedResult?.id) return;

    // Save template content to localStorage for this query
    saveTemplateToLocalStorage();

    // Show progress popup
    setShowProgressPopup(true);
    setProgressStep(1);
    setIsGenerating(true);

    try {
      let apiCompleted = false;
      
      // Start API call immediately
      const apiPromise = (async () => {
        console.log('Generating AI content with notes:', aiNotes.trim());
        
        const response = await generateText({
          notes: aiNotes.trim(),
          queryResultId: selectedResult.id,
          showLoader: false // Disable global loader during multi-step progress
        });
        
        apiCompleted = true;
        
        if (response.generatedText) {
          setAiGeneratedContent(response.generatedText);
          setShowAiSection(false); // Hide AI section after generation
          console.log('AI content generated successfully');
        } else {
          console.warn('No generated text received from API');
          // Fallback to show user something went wrong
          setAiGeneratedContent('Unable to generate content at this time. Please try again.');
          setShowAiSection(false); // Still hide AI section
        }
      })();
      
      // Progress animation in parallel
      (async () => {
        // Step 1: Inspecting domain (4 seconds)
        await new Promise(resolve => setTimeout(resolve, 4000));
        if (apiCompleted) return;
        
        // Step 2: Customizing content
        setProgressStep(2);
        await new Promise(resolve => setTimeout(resolve, 4000));
      })();
      
      // Wait for API to complete
      await apiPromise;
    } catch (error) {
      console.error('Error generating AI content:', error);
      
      // More specific error handling
      if (error.message?.includes('API error')) {
        console.error('AI generation API request failed:', error.message);
      } else {
        console.error('Unknown error during AI content generation:', error.message);
      }
      
      // Show user-friendly error in the text area
      setAiGeneratedContent('Error generating AI content. Please check your connection and try again.');
      setShowAiSection(false); // Still hide AI section
    } finally {
      setShowProgressPopup(false); // Hide progress popup
      setIsGenerating(false);
    }
  };

  const handleSkipToManual = () => {
    // Load the AI notes content into the message textbox if available
    if (aiNotes.trim()) {
      setAiGeneratedContent(aiNotes);
    } else {
      setAiGeneratedContent(''); // Start with empty content for manual entry
    }
    setShowAiSection(false); // Hide AI section
  };

  // Save message content to localStorage
  const handleSaveContent = () => {
    if (aiGeneratedContent.trim()) {
      const domain = getDomainFromUrl(resultUrl);
      const storageKey = `messageContent_${domain}`;
      localStorage.setItem(storageKey, aiGeneratedContent);
      console.log('Message content saved for:', domain);
      
      // Show save confirmation modal
      setShowSaveConfirmation(true);
    }
  };

  // Load message content from localStorage
  const handleLoadContent = () => {
    const domain = getDomainFromUrl(resultUrl);
    const storageKey = `messageContent_${domain}`;
    const savedContent = localStorage.getItem(storageKey);
    if (savedContent) {
      setAiGeneratedContent(savedContent);
      console.log('Message content loaded for:', domain);
    }
  };

  const handleSubmit = async () => {
    if (!selectedProfile || !selectedDestination) return;

    // IMPORTANT: Trigger email/social actions FIRST (synchronously) before any async operations
    // This prevents browsers from blocking popups/navigation that happen after awaits
    if (selectedDestination.type === 'email') {
      // Email: Open default mail client with AI content in new tab
      const mailtoLink = `mailto:${selectedDestination.value}?subject=${encodeURIComponent('Business Inquiry')}&body=${encodeURIComponent(aiGeneratedContent)}`;
      window.open(mailtoLink, '_blank');
    } else if (selectedDestination.type === 'social') {
      // Social: Copy to clipboard, show notification, wait 2 sec, then open
      try {
        await navigator.clipboard.writeText(aiGeneratedContent);
        setShowCopiedNotification(true);
        
        // Wait 2 seconds, then open social profile
        setTimeout(() => {
          window.open(selectedDestination.value, '_blank', 'noopener,noreferrer');
          setShowCopiedNotification(false);
        }, 2000);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        // Still open the profile even if copy fails (after 2 sec)
        setTimeout(() => {
          window.open(selectedDestination.value, '_blank', 'noopener,noreferrer');
        }, 2000);
      }
    }

    setIsSubmitting(true);
    try {
      // Get queryId from URL path (e.g., /query/250ce016-44df-46b9-8c1d-c14d05f806e4)
      const pathname = window.location.pathname;
      const queryId = pathname.startsWith('/query/') ? pathname.split('/query/')[1] : null;
      
      if (queryId && selectedResult?.id) {
        // Save data via API call
        console.log('Saving query setup with data:', {
          queryId: queryId,
          aiNotes: aiNotes,
          queryResultId: selectedResult.id,
          generatedText: aiGeneratedContent
        });
        
        const saveResult = await saveQuerySetup({
          queryId: queryId,
          aiNotes: aiNotes,
          queryResultId: selectedResult.id, // Use actual result ID from selected grid item
          generatedText: aiGeneratedContent
        });

        console.log('Data saved successfully via API:', saveResult);
      } else {
        console.warn('Missing queryId or selectedResult.id:', { queryId, selectedResultId: selectedResult?.id });
      }

      // Handle website destination (requires async chrome extension communication)
      if (selectedDestination.type === 'website') {
        // Only submit to extension if isValid is true
        if (selectedResult?.isValid === true) {
          // Website: Submit to Chrome extension (current behavior)
          await submitToExtension(selectedProfile, resultUrl, aiGeneratedContent);
        } else {
          console.log('Skipping Chrome extension submission: isValid is false, opening URL directly');
          // Open URL in new tab without extension, using result ID as tab name to reuse the same tab
          // Note: Cannot use 'noopener' if we want to reuse the tab by name
          window.open(resultUrl, selectedResult?.id || '_blank');
        }
        // Don't close modal - let user close manually
      }
    } catch (error) {
      console.error('Error submitting:', error);
      
      // Check if it's an extension error that should show the modal
      if (error.showExtensionModal) {
        setShowExtensionModal(true);
        return; // Don't close modal, show extension notice instead
      }
      
      // More specific error handling for other errors
      if (error.message?.includes('JSON')) {
        console.error('API returned invalid JSON response');
      } else if (error.message?.includes('API error')) {
        console.error('API request failed:', error.message);
      } else if (error.message?.includes('Chrome extension')) {
        console.error('Chrome extension communication failed:', error.message);
      } else {
        console.error('Unknown error during submission:', error.message);
      }
      
      // TODO: Show user-friendly error message in UI
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAiNotes('');
    setAiGeneratedContent('');
    setShowAiSection(true); // Reset to show AI section on reopen
    setShowTextbox(false); // Reset to hide textbox on reopen
    setIsLoadingSetup(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <Card className="relative w-full h-full m-0 shadow-lg flex flex-col rounded-none sm:w-[80vw] sm:h-[80vh] sm:max-w-4xl sm:m-4 sm:rounded-lg overflow-hidden">
        <CardHeader className="pb-4 flex-shrink-0 px-8 py-6 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkle className="h-5 w-5 text-primary" />
              Smart Content Generator
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Context Info */}
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-sm font-medium text-foreground">
                {t('selectedProfile') || 'Selected Profile'}:
              </p>
              <button
                onClick={onChangeProfile}
                className="text-sm text-primary font-medium hover:text-primary/80 hover:underline transition-colors cursor-pointer"
                disabled={isLoadingSetup || isSubmitting}
                title="Click to change profile"
              >
                {selectedProfile?.name}
              </button>
            </div>
            <p className="text-sm font-medium text-foreground mb-1">
              {t('submittingResult') || 'Submitting Result'}:
            </p>
            <p className="text-sm text-muted-foreground font-medium break-words">
              {resultTitle}
            </p>
            <a 
              href={resultUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs text-muted-foreground hover:text-foreground break-all"
              title={resultUrl}
            >
              {resultUrl}
            </a>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-hidden flex flex-col pt-6 pb-6">
          <div className="flex-1 overflow-y-auto px-8 flex flex-col space-y-4">
            {/* Loading state for initial setup data */}
            {isLoadingSetup && (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  {t('loadingExistingData') || 'Loading existing data...'}
                </div>
              </div>
            )}
            
            {!isLoadingSetup && (
              <>
            
            {/* AI Notes Input - Only show when showAiSection is true */}
            {showAiSection && (
              <div className="space-y-2 flex-1 flex flex-col">
              <div className="flex items-center justify-between">
                <label htmlFor="aiNotes" className="text-sm font-medium text-foreground">
                  {t('aiNotes') || 'AI Notes'}
                </label>
                <Button 
                  onClick={handleSkipToManual}
                  disabled={isGenerating || isLoadingSetup}
                  variant="ghost"
                  size="sm"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {t('writeManually') || 'Write Manually'}
                </Button>
              </div>
              
                {/* Template Selection Dropdown */}
                <div className="mb-3">
                  <Select 
                    value={selectedTemplate} 
                    onValueChange={handleTemplateSelect}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <SelectTrigger className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                      <SelectValue placeholder={isRTL ? 'בחר תבנית תוכן' : 'Select a content template'} />
                    </SelectTrigger>
                    <SelectContent dir={isRTL ? 'rtl' : 'ltr'}>
                      {getCurrentLanguageTemplates().map((template) => (
                        <SelectItem 
                          key={template.id} 
                          value={template.id}
                          className={`${isRTL ? 'text-right' : 'text-left'}`}
                        >
                          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                            <FileText className={`h-3 w-3 flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <span>{template.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* AI Notes Textbox - Only show when template is selected */}
                {showTextbox && (
                  <>
                    <textarea
                      id="aiNotes"
                      placeholder={t('aiNotesPlaceholder') || 'Draft a message requesting to discuss business opportunities'}
                      value={aiNotes}
                      onChange={(e) => setAiNotes(e.target.value)}
                      disabled={isLoadingSetup}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className={`w-full flex-1 min-h-[150px] p-3 border border-input rounded-md bg-background text-sm resize-vertical focus:outline-none focus:ring-1 focus:ring-inset focus:ring-primary focus:border-primary ${isRTL ? 'text-right' : 'text-left'}`}
                      rows={3}
                    />
                    
                    {/* Action buttons - Generate and Skip */}
                    <div className="flex gap-3">
                      <Button 
                        onClick={handleGenerateContent}
                        disabled={!aiNotes.trim() || !selectedResult?.id || isGenerating || isLoadingSetup}
                        variant="default"
                        size="default"
                        className="font-semibold shadow-lg bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 hover:shadow-xl hover:shadow-purple-200 hover:scale-105 transition-all duration-300"
                      >
                        {isGenerating ? (
                          <>
                            <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                            {t('generating') || 'Generating...'}
                          </>
                        ) : (
                          <>
                            <Sparkle className="h-5 w-5 mr-2" />
                            {`Customize Content for ${getDomainFromUrl(resultUrl)}`}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
            </div>
            )}

            {/* AI Generated Content / Manual Editor - Only show when AI section is hidden */}
            {!showAiSection && (
              <div className="space-y-2 flex-1 flex flex-col">
                <div className="flex items-center justify-between">
                  <label htmlFor="aiGeneratedContent" className="text-sm font-medium text-foreground">
                    {aiGeneratedContent.trim() ? (t('aiGeneratedContent', { domain: getDomainFromUrl(resultUrl) }) || 'AI Generated Content') : (t('messageContent') || 'Message Content')}
                  </label>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost"
                      size="sm"
                      onClick={handleSaveContent}
                      disabled={!aiGeneratedContent.trim() || isLoadingSetup}
                      className="text-sm text-muted-foreground hover:text-foreground"
                      title={t('save') || 'Save'}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {t('save') || 'Save'}
                    </Button>
                    <Button 
                      variant="ghost"
                      size="sm"
                      onClick={handleLoadContent}
                      disabled={isLoadingSetup}
                      className="text-sm text-muted-foreground hover:text-foreground"
                      title={t('load') || 'Load'}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {t('load') || 'Load'}
                    </Button>
                    <Button 
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAiSection(true)}
                      className="text-sm text-muted-foreground hover:text-foreground"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {t('backToAi') || 'Back to AI'}
                    </Button>
                  </div>
                </div>
                <textarea
                  id="aiGeneratedContent"
                  placeholder={aiGeneratedContent.trim() ? "Edit the generated content as needed..." : "Enter your message here..."}
                  value={aiGeneratedContent}
                  onChange={(e) => setAiGeneratedContent(e.target.value)}
                  disabled={isLoadingSetup}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  className={`w-full flex-1 min-h-[200px] p-4 border border-input rounded-md bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${isRTL ? 'text-right' : 'text-left'}`}
                />
              </div>
            )}
              </>
            )}
          </div>

          {/* Destination Selector */}
          {!showAiSection && aiGeneratedContent.trim() && destinationOptions.length > 0 && (
            <div className="px-8 py-4 border-t">
              <label className="block text-sm font-medium mb-2">
                {t('selectDestination')}
              </label>
              <Select
                value={selectedDestination?.value || ''}
                onValueChange={(value) => {
                  const selected = destinationOptions.find(opt => opt.value === value);
                  setSelectedDestination(selected);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('selectDestination')} />
                </SelectTrigger>
                <SelectContent>
                  {destinationOptions.map((option, index) => (
                    <SelectItem key={index} value={option.value}>
                      {option.display}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-4 pt-4 border-t flex-shrink-0 px-8">
            <Button
              variant="outline"
              onClick={handleClose}
              className="w-full sm:w-auto"
              disabled={isSubmitting || isLoadingSetup}
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={disabled || !selectedProfile || showAiSection || !aiGeneratedContent.trim() || isSubmitting || isLoadingSetup}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t('submitting') || 'Submitting...'}
                </>
              ) : (
                t('submit') || 'Submit'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Progress Popup */}
      {showProgressPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" />
          
          {/* Progress Modal */}
          <Card className="relative w-80 mx-4 shadow-xl">
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                {/* Step Icon and Text */}
                <div className="flex items-center justify-center gap-3">
                  {progressStep === 1 ? (
                    <>
                      <Search className="h-6 w-6 text-blue-600 animate-pulse" />
                      <span className="text-lg font-medium">
                        Inspecting {getDomainFromUrl(resultUrl)}
                      </span>
                    </>
                  ) : (
                    <>
                      <Cog className="h-6 w-6 text-purple-600 animate-spin" />
                      <span className="text-lg font-medium">
                        Customizing Content
                      </span>
                    </>
                  )}
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-500 ease-out ${
                      progressStep === 1 ? 'w-1/2' : 'w-full'
                    }`}
                  />
                </div>
                
                {/* Step Indicator */}
                <p className="text-sm text-muted-foreground">
                  Step {progressStep} of 2
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Save Confirmation Modal */}
      {showSaveConfirmation && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 transition-opacity"
            onClick={() => setShowSaveConfirmation(false)}
          />
          
          {/* Modal */}
          <div className="relative w-full max-w-md mx-4 shadow-lg">
            <div className="bg-white rounded-lg">
              <div className="pb-3 flex-shrink-0 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-lg font-semibold">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    {t('saveConfirmation')}
                  </div>
                  <button
                    onClick={() => setShowSaveConfirmation(false)}
                    className="h-6 w-6 p-0 bg-transparent border-none cursor-pointer"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-4 px-6 pb-6">
                <p className="text-sm text-gray-600">
                  Your message content has been saved and can be loaded later.
                </p>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowSaveConfirmation(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    {t('ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extension Notice Modal */}
      <ExtensionNoticeModal
        isOpen={showExtensionModal}
        onClose={() => setShowExtensionModal(false)}
        aiGeneratedContent={aiGeneratedContent}
        resultUrl={resultUrl}
        selectedProfile={selectedProfile}
      />

      {/* Copied Notification */}
      {showCopiedNotification && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-[80] max-w-md">
          <div className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium mb-1">{t('messageCopied')}</p>
                {selectedDestination?.type === 'email' && (
                  <p className="text-sm opacity-90">
                    Please compose an email to: <span className="font-semibold">{selectedDestination.value}</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiContentModal;
